import type { BlockPhase } from '@cadencia/contracts';
import type { Muscle } from '../catalog/muscles.js';
import { classifyChange } from '../decisions/change-policy.js';
import { newDecision, type DecisionRecord } from '../decisions/decision-record.js';
import type { PhaseDecision } from './block-phase.js';
import type { DeloadAssessment } from './deload.js';
import { PROPOSAL_CONFIDENCE } from './proposals.js';
import { WEEKS_FOR_TREND_RULES, type VolumeAdjustment } from './volume-response.js';
import type { ReorganizeDecision } from './week-reorganization.js';

/**
 * Revision semanal y entre bloques (E4 del diagnostico del 21/09/2026).
 *
 * Tres cosas que hasta hoy no tenian sitio:
 *
 * 1. **Las ventanas.** Cuantas semanas mira cada revision vivian como
 *    constantes sueltas dentro de dos servicios de la aplicacion, con el mismo
 *    valor y dos nombres. Una ventana decide tanto como un umbral: con dos
 *    semanas la tendencia de e1RM es ruido, con doce llega tarde. Viven aqui.
 * 2. **El reposo tras un cambio.** Nada impedia subir el volumen de un musculo
 *    una semana y volver a subirlo la siguiente, antes de que el primer cambio
 *    hubiera podido notarse. Eso no es adaptarse al rendimiento: es reaccionar
 *    a la misma señal dos veces.
 * 3. **Mantener el plan como decision.** Una revision que no cambiaba nada no
 *    dejaba rastro, asi que en el historial era indistinguible de una revision
 *    que no se habia hecho. Revisar no obliga a cambiar (§11 del encargo), pero
 *    no cambiar tambien es una decision, y tiene sus motivos.
 *
 * Puro: las fechas entran como texto `YYYY-MM-DD` y el instante de la decision,
 * como parametro.
 */

// ---------------------------------------------------------------------------
// Ventanas
// ---------------------------------------------------------------------------

/**
 * Ventanas de observacion de las revisiones. CONVENCION DECLARADA.
 *
 * * `weeklyHistoryWeeks`: la revision semanal mira la semana que se cierra y las
 *   tres anteriores para la tendencia por musculo, la caida por patron y el
 *   contador de estancamiento. Cuatro es el minimo con el que «dos semanas
 *   seguidas» (`WEEKS_FOR_TREND_RULES`) tiene una semana de contexto antes.
 * * `blockTrendWeeks`: el cierre de bloque mira las cuatro ultimas semanas para
 *   la tendencia de e1RM y del readiness. Es el final del bloque, que es lo que
 *   describe como ha ido; mirar el bloque entero mezclaria la readaptacion del
 *   principio con el estado de ahora.
 * * `volumeSettleWeeks`: revisiones que un musculo tiene que esperar, tras un
 *   cambio de volumen APLICADO, antes de que se le pueda proponer otra subida.
 *   Son tantas como exige una tendencia sostenida, porque es lo que hace falta
 *   para que el cambio anterior se pueda medir: proponer antes seria decidir
 *   con datos que todavia describen el volumen viejo.
 */
export const REVIEW_WINDOWS = {
  weeklyHistoryWeeks: 4,
  blockTrendWeeks: 4,
  volumeSettleWeeks: WEEKS_FOR_TREND_RULES,
} as const;

/**
 * Confianza de «mantener el plan». CONVENCION declarada.
 *
 * Mide lo mismo que `PROPOSAL_CONFIDENCE`: lo firme que es la señal. Con
 * historial, que ninguna regla se cumpla es un resultado; sin historial, no
 * cambiar es solo lo prudente, y la confianza lo dice.
 */
export const KEPT_PLAN_CONFIDENCE = {
  withHistory: 0.8,
  withoutHistory: 0.4,
} as const;

// ---------------------------------------------------------------------------
// Reposo tras un cambio de volumen
// ---------------------------------------------------------------------------

/** El ultimo cambio de volumen que se LLEVO al plan para un musculo. */
export interface AppliedVolumeChange {
  /** Lunes de la semana cuya revision lo propuso. */
  weekStart: string;
  deltaSets: number;
}

/** Semanas enteras entre dos lunes locales. Se ancla en UTC por los dos lados. */
function semanasEntre(desde: string, hasta: string): number {
  const inicio = Date.parse(`${desde}T00:00:00Z`);
  const fin = Date.parse(`${hasta}T00:00:00Z`);
  if (Number.isNaN(inicio) || Number.isNaN(fin)) {
    throw new Error(`Fecha no valida: ${desde} / ${hasta}`);
  }
  return Math.floor((fin - inicio) / (7 * 86_400_000));
}

/**
 * Aplica el reposo a una propuesta de volumen.
 *
 * Solo frena las SUBIDAS. Un recorte por rendimiento cayendo con agujetas es la
 * respuesta a una señal de fatiga, y retrasarlo por estabilidad seria poner la
 * tranquilidad del plan por delante de la recuperacion del atleta. Estable no
 * significa sordo.
 *
 * Cuenta desde la semana que PROPUSO el cambio: la revision de la semana W lo
 * propone, W+1 se entrena con el volumen nuevo, y la revision de W+2 es la
 * primera que tiene dos semanas medidas con el.
 */
export function settleVolumeAdjustment(
  adjustment: VolumeAdjustment,
  lastApplied: AppliedVolumeChange | null,
  weekStart: string,
): VolumeAdjustment {
  if (adjustment.deltaSets <= 0 || lastApplied === null) return adjustment;
  if (semanasEntre(lastApplied.weekStart, weekStart) >= REVIEW_WINDOWS.volumeSettleWeeks) {
    return adjustment;
  }

  return {
    ...adjustment,
    proposedSets: adjustment.currentSets,
    deltaSets: 0,
    reasonCodes: [...adjustment.reasonCodes, 'volume_change_settling'],
    clampedBySafety: false,
  };
}

// ---------------------------------------------------------------------------
// Mantener el plan: la semana
// ---------------------------------------------------------------------------

export interface KeptWeekInput {
  weekStart: string;
  /** Series registradas en la semana revisada. */
  setsLogged: number;
  /** Lo que el motor de volumen contesto, musculo a musculo, sin proponer. */
  volume: readonly Pick<VolumeAdjustment, 'muscle' | 'reasonCodes'>[];
  deload: DeloadAssessment;
  reorganization: ReorganizeDecision;
  stalledExercises: number;
}

/**
 * Registro de una semana revisada en la que el plan sigue igual.
 *
 * `null` cuando no hay nada que registrar: una semana sin una sola serie no
 * permite decir nada del plan, ni para cambiarlo ni para mantenerlo, y una
 * decision de «mantener» con esa base seria una opinion sobre lo que no se ha
 * entrenado. Tambien `null` si alguno de los motores SI tenia algo que decir:
 * quien llama solo debe pedirlo cuando la revision no ha producido nada.
 */
export function keptWeekRecord(input: KeptWeekInput, decidedAt: string): DecisionRecord | null {
  if (input.setsLogged === 0) return null;
  if (input.stalledExercises > 0) return null;
  if (input.deload.action !== 'none' && input.deload.action !== 'halted') return null;
  if (input.reorganization.missedSessionIds.length > 0) return null;

  const codigosDeVolumen = [...new Set(input.volume.flatMap((entrada) => entrada.reasonCodes))];
  const sinHistorial =
    input.volume.length > 0 &&
    input.volume.every((entrada) => entrada.reasonCodes.includes('insufficient_history'));

  const reasonCodes = [
    'plan_kept',
    ...codigosDeVolumen,
    // Con una bandera roja no se propone nada, y no porque todo vaya bien: el
    // motivo tiene que decirlo, o «mantener» pareceria una buena noticia.
    input.deload.action === 'halted' ? 'halted_by_red_flag' : 'deload_score_below_threshold',
    'nothing_missed',
    'no_stalls',
  ];

  return newDecision(
    {
      decisionType: 'plan_kept',
      inputs: {
        weekStart: input.weekStart,
        setsLogged: input.setsLogged,
        musclesReviewed: input.volume.length,
        deloadScore: input.deload.score,
        historyWeeks: REVIEW_WINDOWS.weeklyHistoryWeeks,
      },
      previousValue: null,
      newValue: null,
      source: 'algorithm',
      reasonCodes,
      reasonText: null,
      confidence: sinHistorial
        ? KEPT_PLAN_CONFIDENCE.withoutHistory
        : KEPT_PLAN_CONFIDENCE.withHistory,
      classification: classifyChange({ kind: 'plan_kept' }),
    },
    decidedAt,
  );
}

// ---------------------------------------------------------------------------
// Mantener la estructura: entre bloques
// ---------------------------------------------------------------------------

export interface ContinuedBlockInput {
  previousPhase: BlockPhase;
  decision: PhaseDecision;
  /** La estructura del bloque nuevo la diseño la IA y paso las reglas. */
  aiDesigned: boolean;
  /** Musculos prioritarios que se conservan del bloque que termina. */
  priorityMuscles: readonly Muscle[];
}

/**
 * Registro del bloque que empieza con la MISMA estructura que el anterior.
 *
 * Era el caso mas frecuente de la vida de un usuario —acabar un bloque y
 * seguir con el mismo objetivo— y el unico que no dejaba ninguna decision: un
 * cambio de fase quedaba registrado con sus motivos y continuar no, asi que el
 * historial solo contaba las veces que algo cambiaba.
 *
 * Lanza si la fase cambia: eso es un `phase_change`, que pide aprobacion, y
 * registrarlo aqui como continuacion lo colaria sin ella.
 */
export function continuedBlockRecord(
  input: ContinuedBlockInput,
  decidedAt: string,
): DecisionRecord {
  if (input.decision.change !== null || input.decision.phase !== input.previousPhase) {
    throw new Error('Un cambio de estructura no es una continuacion: es un phase_change');
  }

  return newDecision(
    {
      decisionType: 'block_start',
      inputs: {
        plannedWeeks: input.decision.plannedWeeks,
        priorityMuscles: input.priorityMuscles.length,
        trendWeeks: REVIEW_WINDOWS.blockTrendWeeks,
      },
      previousValue: input.previousPhase,
      newValue: input.decision.phase,
      source: input.aiDesigned ? 'claude' : 'algorithm',
      reasonCodes: [
        'same_phase_continues',
        ...input.decision.reasonCodes,
        input.aiDesigned ? 'structure_designed_by_ai' : 'structure_from_template',
      ],
      reasonText: null,
      confidence: PROPOSAL_CONFIDENCE.phase,
      classification: classifyChange({ kind: 'plan_kept' }),
    },
    decidedAt,
  );
}
